import type { OrdreIntervention } from "types/ordre";
import { isAPrevoir } from "types/ordre";

interface Row {
  id: string;
  num?: string;
  name?: string;
  status?: string;
  modele?: string;
  immatriculation?: string;
  reception?: number | null;
  restitution?: number | null;
  amount?: number | null;
  assigned_to?: string;
  interventions?: OrdreIntervention[];
}

const formatDate = (ms?: number | null) => {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return null;
  }
};

/**
 * Black floating panel shown on row hover in the Ordres table — previews the
 * ordre's interventions and key info without opening the ordre. Mirrors the
 * planning's AppointmentTooltip look. Rendered inside a portal by DataGrid.
 */
export default function OrdreHoverPanel({ row }: { row: Row }) {
  const interventions = row.interventions || [];
  const vehicle = [row.modele, row.immatriculation].filter(Boolean).join(" · ");
  const reception = formatDate(row.reception);
  const restitution = formatDate(row.restitution);

  return (
    <div
      className="bg-neutral-800 text-white rounded-lg px-3 py-2.5 shadow-xl"
      style={{ minWidth: 220, maxWidth: 320 }}
    >
      <div className="flex flex-row items-baseline gap-2">
        {row.num && <span className="text-h5 text-neutral-400 font-medium">OR n°{row.num}</span>}
        <span className="text-h4 font-semibold truncate">{row.name || "—"}</span>
      </div>
      {vehicle && <div className="text-h5 text-neutral-300 mt-0.5">{vehicle}</div>}

      {(reception || restitution) && (
        <div className="flex flex-col gap-0.5 mt-1.5">
          {reception && (
            <div className="text-h5 text-neutral-300">
              <span className="text-neutral-400">Réception :</span> {reception}
            </div>
          )}
          {restitution && (
            <div className="text-h5 text-neutral-300">
              <span className="text-neutral-400">Restitution :</span> {restitution}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-neutral-700">
        <div className="text-h5 text-neutral-400 font-medium mb-1">
          Interventions ({interventions.length})
        </div>
        {interventions.length === 0 ? (
          <div className="text-h5 text-neutral-500 italic">Aucune intervention</div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {interventions.slice(0, 8).map((it) => {
              const aPrevoir = isAPrevoir(it);
              return (
              <div key={it.id} className="flex flex-row items-baseline justify-between gap-2">
                <span className={`text-h5 truncate ${aPrevoir ? "text-amber-300/80 line-through decoration-amber-400/50" : "text-white"}`}>
                  {it.label}{aPrevoir ? " · à prévoir" : ""}
                </span>
                {it.estimated_hours ? (
                  <span className="text-h5 text-neutral-400 flex-shrink-0">{+Number(it.estimated_hours || 0).toFixed(2)}h</span>
                ) : null}
              </div>
              );
            })}
            {interventions.length > 8 && (
              <div className="text-h5 text-neutral-500">+{interventions.length - 8} autre(s)</div>
            )}
          </div>
        )}
      </div>

      {row.amount != null && row.amount > 0 && (
        <div className="mt-2 pt-2 border-t border-neutral-700 flex flex-row items-baseline justify-between">
          <span className="text-h5 text-neutral-400">Montant devis</span>
          <span className="text-h4 font-semibold">{row.amount.toFixed(2)} €</span>
        </div>
      )}
    </div>
  );
}
